import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Eye,
  Calendar,
  DollarSign,
  ShoppingCart,
  Printer,
  FileText,
  TrendingUp,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
  parseISO,
} from "date-fns";
import { useStock } from "@/context/StockContext";
import { useRepairContext } from "@/context/RepairContext";
import { Sale } from "@/types/stock";
import { RepairItem } from "@/types";
import { printSalesTicket } from "./PrintSalesTicket";
import { useRepairShopContext } from "@/context/RepairShopContext";
import { useAuth } from "@/context/AuthContext";

interface SalesReportsProps {
  viewMode: "grid" | "list";
  touchMode: boolean;
  onViewModeChange: (mode: "grid" | "list") => void;
}

type PeriodType = "today" | "week" | "month" | "custom" | "all";
type PaymentType = "sale" | "repair" | "all";

const SalesReports: React.FC<SalesReportsProps> = ({ touchMode }) => {
  const { t } = useTranslation();
  const { sales, loading: salesLoading, deleteSale } = useStock();
  const { repairs, loading: repairsLoading } = useRepairContext();
  const { repairShop } = useRepairShopContext();
  const { roles } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>("today");
  const [selectedType, setSelectedType] = useState<PaymentType>("all");
  const [selectedPayment, setSelectedPayment] = useState<
    Sale | RepairItem | null
  >(null);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState<Sale | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isAdministrator = roles.some((role) => role.role === "administrator");

  const loading = salesLoading || repairsLoading;

  const getDateRange = (period: PeriodType) => {
    const now = new Date();
    switch (period) {
      case "today":
        return { start: startOfDay(now), end: endOfDay(now) };
      case "week":
        return { start: startOfWeek(now), end: endOfWeek(now) };
      case "month":
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case "custom":
        if (customStartDate && customEndDate) {
          return {
            start: startOfDay(parseISO(customStartDate)),
            end: endOfDay(parseISO(customEndDate)),
          };
        }
        return { start: startOfDay(now), end: endOfDay(now) };
      case "all":
        return { start: new Date(0), end: new Date() };
    }
  };

  const filteredData = useMemo(() => {
    const { start, end } = getDateRange(selectedPeriod);

    const filteredSales = sales.filter((sale) =>
      isWithinInterval(new Date(sale.createdAt), { start, end })
    );

    const filteredRepairs = repairs.filter((repair) => {
      const isInPeriod = isWithinInterval(new Date(repair.createdAt), {
        start,
        end,
      });
      const isPaid =
        repair.paymentStatus === "paid" || repair.paymentStatus === "partial";
      return isInPeriod && isPaid;
    });

    return { sales: filteredSales, repairs: filteredRepairs };
  }, [sales, repairs, selectedPeriod, customStartDate, customEndDate]);

  const incomeStats = useMemo(() => {
    const { sales: periodSales, repairs: periodRepairs } = filteredData;

    // Sales income
    const salesRevenue = periodSales.reduce(
      (sum, sale) => sum + sale.totalAmount,
      0
    );
    const salesCost = periodSales.reduce((sum, sale) => {
      const itemsCost =
        sale.items?.reduce((itemSum, item) => {
          return itemSum + (item.product?.cost || 0) * item.quantity;
        }, 0) || 0;
      return sum + itemsCost;
    }, 0);
    const salesProfit = salesRevenue - salesCost;

    // Repairs income - ONLY count repairs marked as "paid"
    // Filter for paid repairs only
    const paidRepairs = periodRepairs.filter((r) => r.paymentStatus === "paid");

    // Calculate repairs revenue including price modifications
    const repairsRevenue = paidRepairs.reduce((sum, repair) => {
      // Start with base repair price
      let finalPrice = repair.repairPrice || 0;

      // Add price modifications if they exist
      if (repair.priceModifications && repair.priceModifications.length > 0) {
        const modificationsTotal = repair.priceModifications.reduce(
          (modSum, mod) => modSum + (mod.amount || 0),
          0
        );
        finalPrice += modificationsTotal;
      }

      return sum + finalPrice;
    }, 0);

    const repairsProfit = repairsRevenue; // Assuming repairs are pure profit

    // Combined
    const totalRevenue = salesRevenue + repairsRevenue;
    const totalCost = salesCost;
    const netIncome = salesProfit + repairsProfit;

    return {
      salesRevenue,
      salesCost,
      salesProfit,
      repairsRevenue,
      repairsProfit,
      totalRevenue,
      totalCost,
      netIncome,
      salesCount: periodSales.length,
      repairsCount: paidRepairs.length, // Count only paid repairs
    };
  }, [filteredData]);

  const handleDeleteClick = (sale: Sale) => {
    setSaleToDelete(sale);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!saleToDelete) return;

    setIsDeleting(true);
    try {
      await deleteSale(saleToDelete.id);
      setDeleteDialogOpen(false);
      setSaleToDelete(null);
      setSelectedPayment(null); // Close details dialog if open
    } catch (error) {
      console.error("Error deleting sale:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const recentPayments = useMemo(() => {
    const { sales: periodSales, repairs: periodRepairs } = filteredData;

    const salesPayments = periodSales.map((sale) => ({
      type: "sale" as const,
      data: sale,
      date: new Date(sale.createdAt),
      amount: sale.totalAmount,
      customer: sale.customerName || t("stock.walkInCustomer"),
      id: sale.id,
    }));

    // Only include repairs that are marked as "paid"
    const paidRepairs = periodRepairs.filter((r) => r.paymentStatus === "paid");

    const repairPayments = paidRepairs.map((repair) => {
      // Calculate final price including modifications
      let finalPrice = repair.repairPrice || 0;
      if (repair.priceModifications && repair.priceModifications.length > 0) {
        const modificationsTotal = repair.priceModifications.reduce(
          (modSum, mod) => modSum + (mod.amount || 0),
          0
        );
        finalPrice += modificationsTotal;
      }

      return {
        type: "repair" as const,
        data: repair,
        date: new Date(repair.createdAt),
        amount: finalPrice,
        customer: repair.customerName,
        id: repair.id,
      };
    });

    const allPayments = [...salesPayments, ...repairPayments].sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );

    if (selectedType === "sale")
      return allPayments.filter((p) => p.type === "sale");
    if (selectedType === "repair")
      return allPayments.filter((p) => p.type === "repair");
    return allPayments;
  }, [filteredData, selectedType, t]);

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} TND`;
  };

  const handlePrintSale = (sale: Sale) => {
    const saleItems =
      sale.items?.map((item) => ({
        id: item.id || `temp-${item.productId}`,
        saleId: sale.id,
        productId: item.productId,
        product: item.product,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount || 0,
        totalPrice: item.totalPrice,
        createdAt: new Date(),
        updatedAt: new Date(),
      })) || [];

    printSalesTicket(sale, saleItems, repairShop);
  };

  const handlePaymentClick = (payment: (typeof recentPayments)[0]) => {
    setSelectedPayment(payment.data);
  };

  const handlePrintReport = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const { start, end } = getDateRange(selectedPeriod);
    const periodLabel =
      selectedPeriod === "custom"
        ? `${format(start, "MMM dd, yyyy")} - ${format(end, "MMM dd, yyyy")}`
        : t(`stock.${selectedPeriod}`);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Sales Report - ${periodLabel}</title>
          <style>
            @page { size: A4; margin: 20mm; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; color: #333; }
            .header { text-align: center; margin-bottom: 30px; }
            .stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px; }
            .stat-card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
            .stat-label { color: #666; font-size: 14px; }
            .stat-value { font-size: 24px; font-weight: bold; color: #333; margin-top: 5px; }
            .breakdown { margin-bottom: 30px; }
            .breakdown h2 { font-size: 18px; margin-bottom: 15px; }
            .breakdown-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${repairShop?.name || "Repair Shop"}</h1>
            <p>Sales Report - ${periodLabel}</p>
            <p>Generated on ${format(new Date(), "MMM dd, yyyy HH:mm")}</p>
          </div>

          <div class="stats">
            <div class="stat-card">
              <div class="stat-label">Total Revenue</div>
              <div class="stat-value">${formatCurrency(
                incomeStats.totalRevenue
              )}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Net Income</div>
              <div class="stat-value">${formatCurrency(
                incomeStats.netIncome
              )}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Sales Income</div>
              <div class="stat-value">${formatCurrency(
                incomeStats.salesRevenue
              )}</div>
              <div class="stat-label">${incomeStats.salesCount} sales</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Repairs Income</div>
              <div class="stat-value">${formatCurrency(
                incomeStats.repairsRevenue
              )}</div>
              <div class="stat-label">${incomeStats.repairsCount} repairs</div>
            </div>
          </div>

          <div class="breakdown">
            <h2>Sales Breakdown</h2>
            <div class="breakdown-item">
              <span>Revenue:</span>
              <span>${formatCurrency(incomeStats.salesRevenue)}</span>
            </div>
            <div class="breakdown-item">
              <span>Cost:</span>
              <span>-${formatCurrency(incomeStats.salesCost)}</span>
            </div>
            <div class="breakdown-item" style="font-weight: bold;">
              <span>Profit:</span>
              <span>${formatCurrency(incomeStats.salesProfit)}</span>
            </div>
          </div>

          <div class="breakdown">
            <h2>Repairs Breakdown</h2>
            <div class="breakdown-item">
              <span>Revenue:</span>
              <span>${formatCurrency(incomeStats.repairsRevenue)}</span>
            </div>
            <div class="breakdown-item" style="font-weight: bold;">
              <span>Profit:</span>
              <span>${formatCurrency(incomeStats.repairsProfit)}</span>
            </div>
          </div>

          <h2>Recent Payments (${recentPayments.length})</h2>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${recentPayments
                .slice(0, 50)
                .map(
                  (payment) => `
                <tr>
                  <td>${payment.type === "sale" ? "Sale" : "Repair"}</td>
                  <td>${payment.customer}</td>
                  <td>${formatCurrency(payment.amount)}</td>
                  <td>${format(payment.date, "MMM dd, yyyy HH:mm")}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>

          <div class="footer">
            <p>${repairShop?.name || "Repair Shop"}</p>
            <p>${repairShop?.address || ""} | ${repairShop?.phone || ""}</p>
          </div>

          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6">
      {/* Period and Type Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Card className="flex-1">
          <CardContent className="p-4">
            <label className="text-sm font-medium mb-2 block">
              {t("stock.selectPeriod")}
            </label>
            <div className="flex gap-2 flex-wrap mb-3">
              <Button
                variant={selectedPeriod === "today" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("today")}
              >
                {t("stock.today")}
              </Button>
              <Button
                variant={selectedPeriod === "week" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("week")}
              >
                {t("stock.thisWeek")}
              </Button>
              <Button
                variant={selectedPeriod === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("month")}
              >
                {t("stock.thisMonth")}
              </Button>
              <Button
                variant={selectedPeriod === "custom" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("custom")}
              >
                {t("stock.custom")}
              </Button>
              <Button
                variant={selectedPeriod === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("all")}
              >
                {t("stock.allTime")}
              </Button>
            </div>
            {selectedPeriod === "custom" && (
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  placeholder="Start Date"
                  className="flex-1"
                />
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  placeholder="End Date"
                  className="flex-1"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="flex-1">
          <CardContent className="p-4">
            <label className="text-sm font-medium mb-2 block">
              {t("stock.paymentType")}
            </label>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={selectedType === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("all")}
              >
                {t("stock.all")}
              </Button>
              <Button
                variant={selectedType === "sale" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("sale")}
              >
                {t("stock.sales")}
              </Button>
              <Button
                variant={selectedType === "repair" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("repair")}
              >
                {t("stock.repairs")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Print Report Button */}
      <div className="flex justify-end">
        <Button onClick={handlePrintReport} className="flex items-center gap-2">
          <Printer className="h-4 w-4" />
          {t("stock.printReport")}
        </Button>
      </div>

      {/* Income Statistics */}
      <div
        className={`grid gap-4 ${
          touchMode ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-4"
        }`}
      >
        <Card>
          <CardContent className={`${touchMode ? "p-6" : "p-4"} text-center`}>
            <DollarSign className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(incomeStats.totalRevenue)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("stock.totalRevenue")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className={`${touchMode ? "p-6" : "p-4"} text-center`}>
            <TrendingUp className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(incomeStats.netIncome)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("stock.netIncome")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className={`${touchMode ? "p-6" : "p-4"} text-center`}>
            <ShoppingCart className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">
              {formatCurrency(incomeStats.salesRevenue)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("stock.salesIncome")} ({incomeStats.salesCount})
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className={`${touchMode ? "p-6" : "p-4"} text-center`}>
            <FileText className="h-8 w-8 text-orange-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">
              {formatCurrency(incomeStats.repairsRevenue)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("stock.repairsIncome")} ({incomeStats.repairsCount})
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Breakdown */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("stock.salesBreakdown")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">{t("stock.revenue")}:</span>
              <span className="font-semibold">
                {formatCurrency(incomeStats.salesRevenue)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t("stock.cost")}:</span>
              <span className="font-semibold text-red-600">
                -{formatCurrency(incomeStats.salesCost)}
              </span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">{t("stock.profit")}:</span>
              <span className="font-bold text-green-600">
                {formatCurrency(incomeStats.salesProfit)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("stock.repairsBreakdown")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">{t("stock.revenue")}:</span>
              <span className="font-semibold">
                {formatCurrency(incomeStats.repairsRevenue)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t("stock.cost")}:</span>
              <span className="font-semibold">-</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">{t("stock.profit")}:</span>
              <span className="font-bold text-green-600">
                {formatCurrency(incomeStats.repairsProfit)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Payments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t("stock.recentPayments")} ({recentPayments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr className="text-left">
                  <th className={`${touchMode ? "p-4" : "p-3"} font-medium`}>
                    {t("stock.type")}
                  </th>
                  <th className={`${touchMode ? "p-4" : "p-3"} font-medium`}>
                    {t("stock.customer")}
                  </th>
                  <th className={`${touchMode ? "p-4" : "p-3"} font-medium`}>
                    {t("stock.amount")}
                  </th>
                  <th className={`${touchMode ? "p-4" : "p-3"} font-medium`}>
                    {t("stock.date")}
                  </th>
                  <th className={`${touchMode ? "p-4" : "p-3"} font-medium`}>
                    {t("stock.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8">
                      {t("common.loading")}...
                    </td>
                  </tr>
                ) : recentPayments.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">
                      {t("stock.noPayments")}
                    </td>
                  </tr>
                ) : (
                  recentPayments.map((payment) => (
                    <tr
                      key={`${payment.type}-${payment.id}`}
                      className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                      onClick={() => handlePaymentClick(payment)}
                    >
                      <td className={`${touchMode ? "p-4" : "p-3"}`}>
                        <Badge
                          variant={
                            payment.type === "sale" ? "default" : "secondary"
                          }
                        >
                          {payment.type === "sale"
                            ? t("stock.sale")
                            : t("stock.repair")}
                        </Badge>
                      </td>
                      <td className={`${touchMode ? "p-4" : "p-3"}`}>
                        <p className="font-medium">{payment.customer}</p>
                      </td>
                      <td
                        className={`${
                          touchMode ? "p-4" : "p-3"
                        } font-semibold text-green-600`}
                      >
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className={`${touchMode ? "p-4" : "p-3"} text-sm`}>
                        <div>
                          <p>{format(payment.date, "MMM dd, yyyy")}</p>
                          <p className="text-gray-500">
                            {format(payment.date, "HH:mm")}
                          </p>
                        </div>
                      </td>
                      <td className={`${touchMode ? "p-4" : "p-3"}`}>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePaymentClick(payment);
                            }}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          {isAdministrator && payment.type === "sale" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(payment.data as Sale);
                              }}
                              title={t("stock.deleteSale")}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Details Dialog */}
      {selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4 max-h-[80vh] overflow-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {selectedPayment && "saleNumber" in selectedPayment
                  ? t("stock.saleDetails")
                  : t("stock.repairDetails")}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPayment(null)}
                >
                  ×
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedPayment && "saleNumber" in selectedPayment ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.saleNumber")}
                      </p>
                      <p className="font-medium">
                        {selectedPayment.saleNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.customer")}
                      </p>
                      <p className="font-medium">
                        {selectedPayment.customerName ||
                          t("stock.walkInCustomer")}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.total")}
                      </p>
                      <p className="font-medium text-green-600">
                        {formatCurrency(selectedPayment.totalAmount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.paymentMethod")}
                      </p>
                      <p className="font-medium">
                        {t(`stock.${selectedPayment.paymentMethod}`)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      {t("stock.items")}
                    </p>
                    <div className="space-y-2">
                      {selectedPayment.items?.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                        >
                          <span>
                            {item.product?.name || "N/A"} x{item.quantity}
                          </span>
                          <span>{formatCurrency(item.totalPrice)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button
                    onClick={() => handlePrintSale(selectedPayment)}
                    className="w-full"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    {t("stock.printTicket")}
                  </Button>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.ticketNumber")}
                      </p>
                      <p className="font-medium">
                        {selectedPayment.ticketNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.customer")}
                      </p>
                      <p className="font-medium">
                        {selectedPayment.customerName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.phoneModel")}
                      </p>
                      <p className="font-medium">
                        {selectedPayment.phoneModel}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.status")}
                      </p>
                      <Badge>{t(`repair.${selectedPayment.status}`)}</Badge>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.repairPrice")}
                      </p>
                      <p className="font-medium text-green-600">
                        {formatCurrency(selectedPayment.repairPrice)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {t("stock.paymentStatus")}
                      </p>
                      <Badge
                        variant={
                          selectedPayment.paymentStatus === "paid"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {t(`repair.${selectedPayment.paymentStatus}`)}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">
                      {t("stock.problemDescription")}
                    </p>
                    <p className="mt-1">{selectedPayment.problemDescription}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              {t("stock.deleteSaleConfirmTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("stock.deleteSaleConfirmMessage")}
            </DialogDescription>
          </DialogHeader>

          {saleToDelete && (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t("stock.deleteSaleWarning")}
                </AlertDescription>
              </Alert>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium">{t("stock.saleNumber")}:</span>
                  <span>{saleToDelete.saleNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{t("stock.total")}:</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(saleToDelete.totalAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{t("stock.items")}:</span>
                  <span>{saleToDelete.items?.length || 0}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? t("common.processing") : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesReports;
